import { checkRecentDeposit, checkRecentRegistration, checkRecentWithdrawal } from '@/api/assets'
import { checkRecentOrder } from '@/api/order'

/**
 * 订单提示音服务
 * 定期检查是否有新的订单、入金、出金、注册记录，并播放提示音
 */
class OrderNotificationService {
  constructor() {
    this.timer = null
    this.checkInterval = 10000 // 每10秒检查一次
    this.audioElements = {
      deposit: null, // 入金音频
      withdrawal: null, // 出金音频
      registration: null, // 注册音频
      order: null // 持仓音频
    }
    this.lastCheckTime = {
      deposit: Date.now(),
      withdrawal: Date.now(),
      registration: Date.now(),
      order: Date.now()
    }
    this.isRunning = false
    this.initAudio()
  }

  /**
   * 初始化音频元素
   */
  initAudio() {
    try {
      // 入金音频 rj.MP3
      this.audioElements.deposit = new Audio('/static/rj.MP3')
      this.audioElements.deposit.preload = 'auto'
      this.audioElements.deposit.volume = 0.7

      // 出金音频 cj.MP3
      this.audioElements.withdrawal = new Audio('/static/cj.MP3')
      this.audioElements.withdrawal.preload = 'auto'
      this.audioElements.withdrawal.volume = 0.7

      // 注册音频 reg.MP3
      this.audioElements.registration = new Audio('/static/reg.MP3')
      this.audioElements.registration.preload = 'auto'
      this.audioElements.registration.volume = 0.7

      // 持仓音频 jy.MP3
      this.audioElements.order = new Audio('/static/jy.MP3')
      this.audioElements.order.preload = 'auto'
      this.audioElements.order.volume = 0.7
    } catch (error) {
      console.error('音频初始化失败:', error)
    }
  }

  /**
   * 播放音频
   * @param {string} type - 音频类型: 'deposit', 'withdrawal', 'registration'
   */
  playAudio(type) {
    try {
      const audio = this.audioElements[type]
      if (audio) {
        // 重置音频到开始位置
        audio.currentTime = 0
        // 播放音频
        const playPromise = audio.play()
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.error(`播放${type}音频失败:`, error)
          })
        }
      }
    } catch (error) {
      console.error(`播放${type}音频失败:`, error)
    }
  }

  /**
   * 检查响应数据中是否有新记录
   * @param {*} response - 接口响应
   * @returns {boolean} - 是否有新记录
   */
  hasNewRecord(response) {
    if (!response || response.code !== 200) {
      return false
    }
    
    // 处理多种可能的返回格式
    const data = response.data
    if (data === true || data === 1) {
      return true
    }
    if (typeof data === 'object' && data !== null) {
      // 如果data是对象，检查常见的字段
      return data.hasNewRecord === true || 
             data.hasNew === true ||
             data.hasRecent === true ||
             (data.count !== undefined && data.count > 0) ||
             (Array.isArray(data) && data.length > 0)
    }
    return false
  }

  /**
   * 检查是否有新入金
   */
  async checkDeposit() {
    try {
      const response = await checkRecentDeposit()
      if (this.hasNewRecord(response)) {
        this.playAudio('deposit')
        this.lastCheckTime.deposit = Date.now()
      }
    } catch (error) {
      // 静默处理错误，避免频繁的错误日志
      // console.error('检查入金记录失败:', error)
    }
  }

  /**
   * 检查是否有新出金
   */
  async checkWithdrawal() {
    try {
      const response = await checkRecentWithdrawal()
      if (this.hasNewRecord(response)) {
        this.playAudio('withdrawal')
        this.lastCheckTime.withdrawal = Date.now()
      }
    } catch (error) {
      // 静默处理错误，避免频繁的错误日志
      // console.error('检查出金记录失败:', error)
    }
  }

  /**
   * 检查是否有新注册
   */
  async checkRegistration() {
    try {
      const response = await checkRecentRegistration()
      if (this.hasNewRecord(response)) {
        this.playAudio('registration')
        this.lastCheckTime.registration = Date.now()
      }
    } catch (error) {
      // 静默处理错误，避免频繁的错误日志
      // console.error('检查注册记录失败:', error)
    }
  }

  /**
   * 检查是否有新持仓
   */
  async checkOrder() {
    try {
      const response = await checkRecentOrder()
      if (this.hasNewRecord(response)) {
        this.playAudio('order')
        this.lastCheckTime.order = Date.now()
      }
    } catch (error) {
      // 静默处理错误，避免频繁的错误日志
      // console.error('检查持仓记录失败:', error)
    }
  }

  /**
   * 执行检查
   */
  async performCheck() {
    if (!this.isRunning) return
    
    // 并行检查所有类型
    await Promise.all([
      this.checkDeposit(),
      this.checkWithdrawal(),
      this.checkRegistration(),
      this.checkOrder()
    ])
  }

  /**
   * 开始监听
   */
  start() {
    if (this.isRunning) {
      console.warn('订单提示音服务已在运行')
      return
    }
    
    this.isRunning = true
    // 立即执行一次检查
    this.performCheck()
    // 设置定时检查
    this.timer = setInterval(() => {
      this.performCheck()
    }, this.checkInterval)
    
    console.log('订单提示音服务已启动')
  }

  /**
   * 停止监听
   */
  stop() {
    if (!this.isRunning) {
      return
    }
    
    this.isRunning = false
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
    
    console.log('订单提示音服务已停止')
  }

  /**
   * 销毁服务
   */
  destroy() {
    this.stop()
    // 清理音频元素
    Object.keys(this.audioElements).forEach(key => {
      if (this.audioElements[key]) {
        this.audioElements[key] = null
      }
    })
  }
}

// 导出单例
export default new OrderNotificationService()




// WEBPACK FOOTER //
// ./src/utils/orderNotification.js